import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from './a11yIIBScreenReaderService';
import settingsReader from '../store/readHelpers/SettingsReader';

const matchInteractionA11yService = {
    /**
     * Checks if accessibility improvements for match interaction are enabled (v3).
     * @returns {boolean}
     */
    isMatchInteractionA11yEnabled() {
        return a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);
    },

    /**
     * Returns the appropriate tab index for row headers based on accessibility setting.
     * @param {number|undefined} originalValue - The original tab index value
     * @param {number|undefined} newValue - The new tab index value for accessibility mode
     * @returns {number|undefined}
     */
    getRowHeaderTabIndex(originalValue, newValue = 0) {
        return this.isMatchInteractionA11yEnabled() ? newValue : originalValue;
    },

    /**
     * Returns data-question-id attribute object if accessibility is enabled.
     * @param {string} questionIdentifier - The question identifier
     * @returns {Object} Attribute object to spread on element, or empty object
     */
    getQuestionIdAttributes(questionIdentifier) {
        return this.isMatchInteractionA11yEnabled()
            ? { 'data-question-id': questionIdentifier }
            : {};
    },

    /**
     * Helper function to get row navigation data robustly.
     * @param {HTMLElement} currentRow - The current table row
     * @param {number} direction - Direction of movement (-1 for previous, 1 for next)
     * @returns {Object} Navigation data including rows, indices, and target row
     */
    getRowNavigationData(currentRow, direction) {
        // Find the closest table body or table container for robust navigation
        const tableContainer = currentRow.closest('tbody') || currentRow.closest('table');

        if (!tableContainer) {
            return {
                rows: [],
                currentIndex: -1,
                targetIndex: -1,
                targetRow: null,
                isValidTarget: false,
            };
        }

        // Query for actual table rows to ensure we only get TR elements
        const rows = Array.from(tableContainer.querySelectorAll('tr'));
        const currentIndex = rows.indexOf(currentRow);

        // Validate that we found the current row
        if (currentIndex === -1) {
            return {
                rows,
                currentIndex: -1,
                targetIndex: -1,
                targetRow: null,
                isValidTarget: false,
            };
        }

        const targetIndex = currentIndex + direction;
        const isValidTarget = targetIndex >= 0 && targetIndex < rows.length;
        const targetRow = isValidTarget ? rows[targetIndex] : null;

        return {
            rows,
            currentIndex,
            targetIndex,
            targetRow,
            isValidTarget,
        };
    },
    /**
     * Helper function to find the target radio button (checked or first).
     * @param {NodeList|Array} radioButtons - List of radio button elements
     * @returns {HTMLElement|null} The target radio button to focus
     */
    findTargetRadioButton(radioButtons) {
        if (radioButtons.length === 0) {
            return null;
        }
        const checkedRadio = Array.from(radioButtons).find(radio => radio.checked);
        return checkedRadio || radioButtons[0];
    },
    /**
     * Handles focus movement to the next/previous row in enhanced accessibility mode.
     * @param {HTMLElement} currentRow - The current table row
     * @param {number} direction - Direction of movement (-1 for previous, 1 for next)
     * @returns {boolean} True if focus was successfully moved, false otherwise
     */
    handleFocusToNextRow(currentRow, direction) {
        if (!this.isMatchInteractionA11yEnabled()) {
            return false;
        }

        const { targetRow, isValidTarget } = this.getRowNavigationData(currentRow, direction);

        if (isValidTarget) {
            // Priority 1: Focus on row header (th) first
            const rowHeader = targetRow.querySelector('th[tabindex]');
            if (rowHeader) {
                rowHeader.focus();
                return true;
            }

            // Priority 2: Focus on checked radio button or first radio button
            const radioButtons = targetRow.querySelectorAll('input[type="radio"]');
            if (radioButtons.length > 0) {
                const targetRadio = this.findTargetRadioButton(radioButtons);
                targetRadio.focus();
                return true;
            }

            // Priority 3: Fallback to other focusable elements
            const focusables = settingsReader.getFocusableElements(targetRow);
            if (focusables.length > 0) {
                const target = direction === -1 ? focusables[focusables.length - 1] : focusables[0];
                target.focus();
                return true;
            }
        }
        return false;
    },

    /**
     * Handles focus movement from radio button based on tab direction.
     * @param {HTMLElement} currentRow - The current table row
     * @param {number} direction - Direction of movement (-1 for previous, 1 for next)
     * @returns {boolean} True if focus was handled, false to continue with default behavior
     */
    handleRadioButtonTabNavigation(currentRow, direction) {
        if (!this.isMatchInteractionA11yEnabled()) {
            return false;
        }

        if (direction === -1) {
            // Shift+Tab from radio button: go to same row's header
            const rowHeader = currentRow.querySelector('th[tabindex]');
            if (rowHeader) {
                rowHeader.focus();
                return true;
            }
        } else {
            // Tab forward from radio button: go to next row's header
            const { targetRow, isValidTarget } = this.getRowNavigationData(currentRow, 1);

            if (isValidTarget) {
                const rowHeader = targetRow.querySelector('th[tabindex]');
                if (rowHeader) {
                    rowHeader.focus();
                    return true;
                }
            } else {
                // Exit table if no next row
                settingsReader.getFocusOutsideTable(direction);
                return true;
            }
        }
        return false;
    },

    /**
     * Handles focus movement from row header based on tab direction.
     * @param {HTMLElement} currentRow - The current table row
     * @param {number} direction - Direction of movement (-1 for previous, 1 for next)
     * @returns {boolean} True if focus was handled, false to continue with default behavior
     */
    handleRowHeaderTabNavigation(currentRow, direction) {
        if (!this.isMatchInteractionA11yEnabled()) {
            return false;
        }

        if (direction === 1) {
            // Tab forward from row header: go to first radio button in same row
            const radioButtons = currentRow.querySelectorAll('input[type="radio"]');
            if (radioButtons.length > 0) {
                const targetRadio = this.findTargetRadioButton(radioButtons);
                targetRadio.focus();
                return true;
            }
        } else {
            // Shift+Tab from row header: go to previous row's radio button
            const { targetRow, isValidTarget } = this.getRowNavigationData(currentRow, -1);

            if (isValidTarget) {
                const radioButtons = targetRow.querySelectorAll('input[type="radio"]');
                if (radioButtons.length > 0) {
                    const targetRadio = this.findTargetRadioButton(radioButtons);
                    targetRadio.focus();
                    return true;
                }
            } else {
                // Exit table if no previous row
                settingsReader.getFocusOutsideTable(direction);
                return true;
            }
        }
        return false;
    },

    /**
     * Handles focus within the current row based on direction.
     * @param {HTMLElement} currentRow - The current table row
     * @param {HTMLElement} activeElement - Currently focused element
     * @param {number} direction - Direction of movement (-1 for previous, 1 for next)
     * @returns {boolean} True if focus was handled within row, false to move to next/previous row
     */
    handleFocusWithinCurrentRow(currentRow, activeElement, direction) {
        // Filter focusable elements: include all non-radio elements, but for radio buttons,
        // only include checked ones to maintain proper Tab order (unchecked radios are reached via arrow keys)
        const focusables = settingsReader.getFocusableElements(currentRow)
            .filter(el => el.type !== 'radio' || el.checked);
        const currentIndex = focusables.indexOf(activeElement);
        const nextIndex = currentIndex + direction;

        if (nextIndex >= 0 && nextIndex < focusables.length) {
            focusables[nextIndex].focus();
            return true;
        }
        return false;
    },

    /**
     * Handles special flagged item early jump behavior.
     * @param {number} direction - Direction of movement
     * @param {boolean} isFlaggingEnabled - Whether flagging is enabled
     * @param {HTMLElement} activeElement - Currently focused element
     * @param {boolean} isVisited - Whether the item has been visited
     * @returns {boolean} True if early jump should occur
     */
    shouldPerformFlaggedEarlyJump(direction, isFlaggingEnabled, activeElement, isVisited) {
        const isFlagFocused = activeElement && activeElement.tagName.toLowerCase() === 'button';
        return direction === 1 && isFlaggingEnabled && isFlagFocused && !isVisited;
    },

    /**
     * Helper to handle navigation result with common cleanup actions.
     * @private
     */
    handleNavigationResult(e, handled, setFlaggedItemCount, flaggedItems) {
        if (handled) {
            e.preventDefault();
            setFlaggedItemCount(flaggedItems.length);
        }
        return handled;
    },

    /**
     * Enhanced tab handling for match interaction accessibility.
     * @param {KeyboardEvent} e - The keyboard event
     * @param {HTMLElement} currentRow - The current table row
     * @param {Function} setFlaggedItemCount - Function to set flagged item count
     * @param {Array} flaggedItems - Array of flagged items
     * @param {boolean} isFlaggingEnabled - Whether flagging is enabled
     * @param {boolean} isVisited - Whether the item has been visited
     * @returns {boolean} True if event was handled, false to use original behavior
     */
    handleEnhancedTabNavigation(e, currentRow, setFlaggedItemCount, flaggedItems, isFlaggingEnabled, isVisited) {
        if (!this.isMatchInteractionA11yEnabled()) {
            return false;
        }

        try {
            const direction = e.shiftKey ? -1 : 1;
            const activeElement = document.activeElement;

            // Handle radio button focus
            if (activeElement && activeElement.type === 'radio') {
                return this.handleNavigationResult(
                    e,
                    this.handleRadioButtonTabNavigation(currentRow, direction),
                    setFlaggedItemCount,
                    flaggedItems
                );
            }

            // Handle row header focus
            if (activeElement && typeof activeElement.tagName === 'string' && activeElement.tagName.toLowerCase() === 'th') {
                return this.handleNavigationResult(
                    e,
                    this.handleRowHeaderTabNavigation(currentRow, direction),
                    setFlaggedItemCount,
                    flaggedItems
                );
            }

            // Handle flagged early jump
            if (this.shouldPerformFlaggedEarlyJump(direction, isFlaggingEnabled, activeElement, isVisited)) {
                return this.handleNavigationResult(
                    e,
                    this.handleFocusToNextRow(currentRow, direction),
                    setFlaggedItemCount,
                    flaggedItems
                );
            }

            // Move focus within current row
            if (this.handleFocusWithinCurrentRow(currentRow, activeElement, direction)) {
                return this.handleNavigationResult(e, true, setFlaggedItemCount, flaggedItems);
            }

            // Move to next/previous row or exit table
            if (!this.handleFocusToNextRow(currentRow, direction)) {
                settingsReader.getFocusOutsideTable(direction);
            }

            return this.handleNavigationResult(e, true, setFlaggedItemCount, flaggedItems);
        } catch (error) {
            // Log error for debugging in development only; don't prevent default tab behavior
            if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
                console.warn('Enhanced tab navigation failed, falling back to default behavior:', error);
            }
            return false;
        }
    },

    /**
     * Handles keyboard navigation for radio buttons within match interaction rows.
     * @param {KeyboardEvent} e - The keyboard event
     * @param {boolean} isReadOnly - Whether the radio button is read-only
     * @param {boolean} checked - Whether the radio button is currently checked
     * @param {Function} handleUserResponse - Function to handle user response
     * @param {string} value - Value of the radio button
     * @returns {boolean} True if event was handled, false otherwise
     */
    handleRadioButtonKeyDown(e, isReadOnly, checked, handleUserResponse, value) {
        if (!this.isMatchInteractionA11yEnabled() || isReadOnly) {
            return false;
        }

        const currentRow = e.target.closest('tr');
        if (!currentRow) {
            return false;
        }

        const radioButtons = Array.from(currentRow.querySelectorAll('input[type="radio"]'));
        const currentIndex = radioButtons.indexOf(e.target);

        switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown': {
            e.preventDefault();
            const nextIndex = currentIndex < radioButtons.length - 1 ? currentIndex + 1 : 0;
            radioButtons[nextIndex].focus();
            return true;
        }
        case 'ArrowLeft':
        case 'ArrowUp': {
            e.preventDefault();
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : radioButtons.length - 1;
            radioButtons[prevIndex].focus();
            return true;
        }
        case ' ': {
            e.preventDefault();
            if (!checked) {
                const fakeEvent = {
                    currentTarget: { value },
                };
                handleUserResponse(fakeEvent);
            }
            return true;
        }
        default:
            return false;
        }
    },

};

export default matchInteractionA11yService;
